/**
 * 연간진도표·프로그램 진도표 공통: ArrowUp/Down 시 sticky thead 아래~래퍼 하단 안에 선택 행이 들어오도록 스크롤.
 * (가상 스크롤이 없는 표는 flushSync 없이 이 함수만 호출하면 됨)
 */

export const KEYBOARD_TABLE_ARROW_UP_TOP_INSET_PX = 14;

export type KeyboardTableVerticalArrow = 'ArrowUp' | 'ArrowDown';

export function scrollTableWrapForVerticalKeyboardArrow(params: {
  wrap: HTMLDivElement;
  thead: HTMLElement | null;
  /** thead offsetHeight 를 못 읽을 때 */
  fallbackHeaderPx: number;
  anchorMap: Map<string, HTMLTableCellElement>;
  anchorKey: string;
  rowIdx: number;
  nominalRowHeight: number;
  keyboardArrow: KeyboardTableVerticalArrow;
}): void {
  const {
    wrap,
    thead,
    fallbackHeaderPx,
    anchorMap,
    anchorKey,
    rowIdx,
    nominalRowHeight,
    keyboardArrow,
  } = params;

  if (rowIdx < 0) return;

  const measuredHead = thead?.offsetHeight ?? 0;
  const headerHeight = measuredHead > 0 ? measuredHead : Math.max(fallbackHeaderPx, 0);
  const h = Math.max(1, nominalRowHeight);
  const rowTop = headerHeight + rowIdx * h;
  const rowBottom = rowTop + h;
  const V = wrap.clientHeight;
  const maxTop = Math.max(0, wrap.scrollHeight - V);

  let st = wrap.scrollTop;
  for (let guard = 0; guard < 120; guard += 1) {
    wrap.scrollTop = st;
    const wrapRect = wrap.getBoundingClientRect();
    const headBottomMeasured = thead ? thead.getBoundingClientRect().bottom : wrapRect.top + headerHeight;
    const headBottomFromWrap = wrapRect.top + headerHeight;
    const headBottom =
      keyboardArrow === 'ArrowUp'
        ? Math.max(headBottomMeasured, headBottomFromWrap)
        : headBottomMeasured;
    const contentTopMin =
      keyboardArrow === 'ArrowUp' ? headBottom + KEYBOARD_TABLE_ARROW_UP_TOP_INSET_PX : headBottom;
    const wrapBottom = wrapRect.bottom;
    const el = anchorMap.get(anchorKey) ?? null;

    if (el) {
      const tr = el.closest('tr');
      const rawRowH =
        tr instanceof HTMLTableRowElement ? tr.getBoundingClientRect().height : el.getBoundingClientRect().height;
      const roundedH = Math.round(rawRowH);
      const step = Number.isFinite(roundedH) && roundedH > 0 ? roundedH : h;
      const cr = el.getBoundingClientRect();
      if (cr.top >= contentTopMin - 0.5 && cr.bottom <= wrapBottom + 0.5) break;
      const prev = st;
      if (keyboardArrow === 'ArrowUp' && cr.top < contentTopMin) {
        st = Math.max(0, st - step);
      } else if (keyboardArrow === 'ArrowDown' && cr.bottom > wrapBottom) {
        st = Math.min(maxTop, st + step);
      } else {
        break;
      }
      if (st === prev) break;
      continue;
    }

    const visibleTop =
      keyboardArrow === 'ArrowUp'
        ? st + headerHeight + KEYBOARD_TABLE_ARROW_UP_TOP_INSET_PX
        : st + headerHeight;
    const visibleBottom = st + V;
    if (rowTop >= visibleTop && rowBottom <= visibleBottom) break;
    const prev = st;
    if (keyboardArrow === 'ArrowUp' && rowTop < visibleTop) {
      st = Math.max(0, st - h);
    } else if (keyboardArrow === 'ArrowDown' && rowBottom > visibleBottom) {
      st = Math.min(maxTop, st + h);
    } else {
      break;
    }
    if (st === prev) break;
  }
  wrap.scrollTop = st;
}
